/**
 * Export einer Notation als hochauflösende PNG-Grafik.
 *
 * Für ein gut lesbares Proben-Leadsheet wird bewusst schwarz auf weiß
 * gerastert – unabhängig vom Farbschema der Seite. Dies ist die einzige
 * Stelle mit Canvas-Bezug; die reinen Helfer (Dateiname) sind separat
 * testbar.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface PngExportOptions {
  /** Skalierungsfaktor für die Auflösung (Standard 3× für A4/Druck). */
  readonly scale?: number;
  /** Hintergrundfarbe der Grafik. */
  readonly background?: string;
  /** Tintenfarbe (ersetzt `currentColor`). */
  readonly ink?: string;
  /** Dateiname des Downloads. */
  readonly filename?: string;
}

const DEFAULT_OPTIONS = {
  scale: 3,
  background: '#ffffff',
  ink: '#111318',
  filename: 'drumpattern.png',
} as const;

/**
 * Leitet aus einem Titel einen sicheren Dateinamen ab.
 * Ergebnis ist immer nicht leer und endet auf `.png`.
 */
export function leadsheetFilename(title: string): string {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'drumpattern'}.png`;
}

function readViewBox(svg: SVGSVGElement): { width: number; height: number } {
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.split(/\s+/).map(Number);
    const width = parts[2];
    const height = parts[3];
    if (width && height && Number.isFinite(width) && Number.isFinite(height)) {
      return { width, height };
    }
  }
  return {
    width: Number(svg.getAttribute('width')) || 0,
    height: Number(svg.getAttribute('height')) || 0,
  };
}

function svgToDataUrl(svg: SVGSVGElement, ink: string): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', SVG_NS);
  // `currentColor` durch die feste Tintenfarbe ersetzen.
  clone.style.color = ink;
  const xml = new XMLSerializer().serializeToString(clone);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      resolve(image);
    };
    image.onerror = () => {
      reject(new Error('SVG konnte nicht als Bild geladen werden.'));
    };
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('PNG konnte nicht erzeugt werden.'));
      }
    }, 'image/png');
  });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Rastert eine Notation und stößt den Download als PNG an.
 */
export async function exportSvgAsPng(
  svg: SVGSVGElement,
  options: PngExportOptions = {},
): Promise<void> {
  const { scale, background, ink, filename } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };
  const { width, height } = readViewBox(svg);
  if (width <= 0 || height <= 0) {
    throw new Error('Ungültige Grafikabmessungen für den Export.');
  }

  const image = await loadImage(svgToDataUrl(svg, ink));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas-Kontext nicht verfügbar.');
  }
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  triggerDownload(await canvasToBlob(canvas), filename);
}
